import { Injectable } from '@angular/core';
import { DTypeFormTriggerField } from '@grupo-constant/next-dtype/form';
import { DTypeCol, DTypeRepository, DTypeStatus, NextLocator, TriggerEvent, TriggerResponsible, TriggerShortcutExpr } from '@grupo-constant/next-dtype/types';
import { formatISO, startOfToday } from 'date-fns';
import { catchError, firstValueFrom, map, Observable, throwError } from 'rxjs';
import { CompanyService } from '../company/company.service';
import { DTypeService } from '../dtype/dtype.service';
import { NextMethodsEnum } from '../enums/next-methods.enum';
import { HttpService } from '../http/http.service';
import { NextTaskTypeForm } from '../task-type/task-type.types';
import { Task } from '../../modules/admin/tasks/tasks.types';
import { UserService } from '../user/user.service';
import { FlushFormBody, FlushFormReply, Form, RetrieveNewFormInstaceBody, RetrieveNewFormInstanceReply } from './form.types';
import { DownloadMasterXSSFBody } from '../master-data/master-data.types';

export const FORM_URI = 'dtype://com.cloudcodice.feature.dtype.facade.form.Form';

@Injectable({
    providedIn: 'root'
})
export class FormService {

    constructor(
        private companyService: CompanyService,
        private dtypeService: DTypeService,
        private httpService: HttpService,
        private userService: UserService
    ) { }

    retrieveFormDType() {
        return this.dtypeService.getDatatype({ retrieveDTypeBody: { uri: FORM_URI, status: DTypeStatus.DESIGN, bindableOptions: true }, includeEmbedded: true });
    }

    retrieveFormDTypeByUri(uri: string, status: DTypeStatus, locator?: NextLocator): Promise<Form> {
        return this.dtypeService.getDatatype({ retrieveDTypeBody: { uri, status, locator, bindableOptions: true }, includeEmbedded: true });
    }

    retrieveFormDTypeRepositoryByUri(uri: string, status: DTypeStatus, locator?: NextLocator): Observable<DTypeRepository> {
        return this.dtypeService.retrieveDTypeRepositoryByUri({ uri, status, locator, bindableOptions: true });
    }

    retrieveFormDtypeForm(values: any, options?: { readonly?: boolean; isUpdate?: boolean }) {
        return this.dtypeService.getDtypeFormByUri({ uri: FORM_URI, status: DTypeStatus.DESIGN, bindableOptions: true }, values, options);
    }

    retrieveNewFormInstance(body: RetrieveNewFormInstaceBody) {
        return this.httpService.post<RetrieveNewFormInstanceReply>(NextMethodsEnum.retrieveNewFormInstance, body);
    }

    flushForm(form: Form, locator: NextLocator) {
        return this.dtypeService.flushDType({ locator, facadeUri: FORM_URI, dtype: form });
    }

    flushFormData(body: FlushFormBody) {
        return this.httpService.post<FlushFormReply>(NextMethodsEnum.flushForm, body)
            .pipe(
                catchError((error) => {
                    console.error(error);
                    return throwError(() => error.error);
                }),
                map(reply => reply.taskFormSummary)
            );
    }

    checkTriggerField(params: {
        value?: any;
        triggerField: DTypeFormTriggerField;
        event: TriggerEvent | TriggerEvent[];
    }): boolean {
        let needsToBeFilled = false;
        if (((params?.value === null || params?.value === undefined) &&
            params?.triggerField?.responsible === TriggerResponsible.FIRST) ||
            params?.triggerField?.responsible === TriggerResponsible.CLIENT) {
            if (typeof params.event === 'string' && params.triggerField.event === params.event) {
                needsToBeFilled = (params?.value === null || params?.value === undefined) ||
                    params.event === TriggerEvent.ON_OPEN_RESET || params.event === TriggerEvent.ON_QUEUE_RESET;
            } else if (Array.isArray(params.event)) {
                for (const event of params.event) {
                    if (params.triggerField.event === event) {
                        if ((params?.value === null || params?.value === undefined) ||
                            event === TriggerEvent.ON_OPEN_RESET || event === TriggerEvent.ON_QUEUE_RESET) {
                            needsToBeFilled = true;
                            break;
                        }
                    }
                }
            }
        }

        return needsToBeFilled;
    }

    async getTriggerFieldValue(triggerField: DTypeFormTriggerField, form?: NextTaskTypeForm, task?: Task) {
        let value: any = null;
        switch (triggerField.shortcutExpr) {
            case TriggerShortcutExpr.userId:
                const user = await firstValueFrom(this.userService.get());
                value = user?.userId;
                break;
            case TriggerShortcutExpr.companyId:
                const company = await firstValueFrom(this.companyService.get());
                value = company.companyId;
                break;
            case TriggerShortcutExpr.ISODatetime:
            case TriggerShortcutExpr.ISOTime:
                value = formatISO(new Date());
                break;
            case TriggerShortcutExpr.ISODate:
                value = formatISO(startOfToday());
                break;
            case TriggerShortcutExpr.formRelease:
                value = form.formRelease;
                break;
            case TriggerShortcutExpr.serviceId:
                value = task?.serviceId;
                break;
            case TriggerShortcutExpr.taskId:
                value = task?.taskId;
                break;
            case TriggerShortcutExpr.taskTypeUri:
                value = task?.taskTypeUri;
                break;
            case TriggerShortcutExpr.formUri:
                value = form?.formUri;
                break;
            case TriggerShortcutExpr.taskTypeExtraPayload:
                value = triggerField.field ? task?.serverPayload?.extraPayload?.[triggerField.field] : undefined;
                break;
            default:
                break;
        }
        return value;
    }

    updateForm(repo: DTypeRepository, goLive?: boolean) {
        this.dtypeService.updateDType(repo.uri, repo, goLive);
    }

    updateLive(uri: string) {
        return this.dtypeService.forwardDTypeToLive({uri});
    }

    emitUpdateSidebarEvent() {
        this.dtypeService.emitUpdateSidebarEvent();
    }

    updateColumns(columns: DTypeCol[]) {
        this.dtypeService.columns$.next(columns);
    }

    getLocatorFromString(stringLocator: string): NextLocator {
        return this.dtypeService.getLocatorFromString(stringLocator);
    }

    downloadStructureXSSF(body: DownloadMasterXSSFBody): any {
        return this.httpService.postWithHeaders<any>(NextMethodsEnum.downloadNextMasterFormStructureXSSF, body, 'blob');
    }

}
